import React, { useEffect } from "react";
import { ThemeProvider } from "@mui/material/styles";
import theme from "./theme/theme";
import Router from "./framework/Router";
import { AlertProvider } from "./context/AlertContext";
import TransitionAlerts from "./utils/Alert/index"
import SubscriptionAlert from "./utils/Alert/SubscriptionAlert";
import { ProfileProvider } from "./context/ProfileContext";
import { FormProvider } from "./context/FormContext";
import { refreshDateFormat } from "./utils/dateFormatStore";
import "./api/interceptors"; // installs the global EMAIL_NOT_VERIFIED 403 gate

const App = () => {

  useEffect(() => {
    refreshDateFormat();
  }, []);

  const queryParams = new URLSearchParams(window.location.search);
  const planId = queryParams.get("planId");
  const price = queryParams.get("price");
  const subscriptionId = queryParams.get("subscriptionId") || queryParams.get("subscriptioni");

  // Payment return: after a Paystack transaction the backend redirects to the app
  // with ?payment=success|failed. Update the subscription flags in localStorage
  // synchronously here (before the rest of the app mounts) so SubscriptionAlert
  // renders from the correct state instead of the stale pre-payment values.
  // getProfileView() still runs on the landing page and reconciles the
  // authoritative values from the backend right after.
  const paymentResult = queryParams.get("payment");
  if (paymentResult === "success") {
    localStorage.setItem("is_subscription", "true");
    localStorage.setItem("isExpired", "false");
    localStorage.setItem("isTrialExpired", "false");
  }
  // Stripping the ?payment param + the success toast/reconcile is owned by
  // <PaymentReturnHandler /> (mounted in the Router), so it can run inside the
  // provider tree. We only seed the flags synchronously above to avoid a flash
  // of the stale SubscriptionAlert before the handler mounts.

  if (planId && (price || subscriptionId)) {
    const accessToken = localStorage.getItem("accessToken");

    if (accessToken) {
      // If user is already logged in, navigate to the dashboard profile screen immediately
      // Using replace to avoid a flash of the current page and to keep history clean
      window.location.replace("/dashboard/profile");
      return null; // Stop rendering here to ensure Router logic doesn't interfere
    } else {
      // Force Logout for new users to ensure a fresh session
      localStorage.clear();
      sessionStorage.clear();

      // Persistence: store parameters to persist through login/signup flow
      if (planId) localStorage.setItem("planId", planId);
      if (planId) sessionStorage.setItem("planId", planId);
      if (price) sessionStorage.setItem("price", price);
      if (subscriptionId) sessionStorage.setItem("subscriptionId", subscriptionId);

      // Redirection: automatically redirect to home page (which is the login screen)
      window.location.replace("/");
      return null;
    }
  }

  return (
    <ThemeProvider theme={theme}>
      <ProfileProvider>
        <AlertProvider>
          <FormProvider>
            <TransitionAlerts/>
            <Router />
          </FormProvider>
        </AlertProvider>
      </ProfileProvider>
    </ThemeProvider>
  );
}

export default App;
